import {
  Button,
  TableBody,
  TableHead,
  Table,
  TableSortLabel,
  FormControlLabel,
  IconButton,
  Stack,
  Tooltip,
  Collapse,
  TableContainer,
  Typography,
} from "@mui/material";
import { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { NavLink } from "react-router";
import { orderBy } from "lodash";
import ShowTransaction from "./ShowTransaction";
import {
  StyledCell,
  StyledRow,
} from "../../styled-components/styledTableComponents";
import { Switch as GuiSwitch } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import Paper from "@mui/material/Paper";
import { TransactionDTO } from "../../generated/api";
import { TECHNICAL } from "../../util/constants";
import { isTransactionEditable } from "../../util/PrivilegeUtil";

export default function Transactions() {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const extendedCard = useSelector((state: any) => state.card.extendedCard);
  const session = useSelector((state: any) => state.session);

  const [sortedList, setList] = useState<TransactionDTO[] | null>(null);
  const [expiredList, setExpiredList] = useState<TransactionDTO[] | null>(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [column, setColumn] = useState([""]);
  const [showInactive, setShowInactive] = useState(true);
  const [showChanged, setShowChanged] = useState(false);

  const returnDirection = () => {
    if (sortDirection === "desc") {
      return "desc";
    }
    return "asc";
  };

  useEffect(() => {
    setColumn(["transactionDate", "asc"]);
    setSortDirection("asc");
  }, [extendedCard]);

  const invertDirect = (direction: any) => {
    if (direction === "asc") {
      setSortDirection("desc");
      return "desc";
    } else {
      setSortDirection("asc");
      return "asc";
    }
  };

  useEffect(() => {
    const handleSort = (params: string[]) => {
      if (extendedCard) {
        setList(
          orderBy(
            [...extendedCard.transactions],
            params[0],
            invertDirect(sortDirection)
          )
        );
        setExpiredList(
          orderBy(
            [...extendedCard.expiredTransactions],
            params[0],
            invertDirect(sortDirection)
          )
        );
      }
    };
    handleSort(column);
  }, [column]); //eslint-disable-line react-hooks/exhaustive-deps

  const deleteTransaction = (transaction: any) => {
    dispatch({ type: "DELETE_TRANSACTION_MODAL", transaction });
  };

  const updateTransaction = (transaction: any) => {
    dispatch({ type: "UPDATE_TRANSACTION_MODAL", transaction });
  };

  function Head() {
    return (
      <TableHead sx={{ display: "table-header-group" }}>
        <StyledRow>
          <StyledCell sx={{ fontWeight: "bold" }}>
            <TableSortLabel
              active={column[0] === "careProvider"}
              onClick={() => setColumn(["careProvider", sortDirection])}
              direction={returnDirection()}
            >
              {t("careProvider")}
            </TableSortLabel>
          </StyledCell>
          <StyledCell
            onClick={() => setColumn(["transactionDate", sortDirection])}
            sx={{ fontWeight: "bold" }}
          >
            <TableSortLabel
              active={column[0] === "transactionDate"}
              onClick={() => setColumn(["transactionDate", sortDirection])}
              direction={returnDirection()}
            >
              {extendedCard?.cardType?.id.includes(TECHNICAL)
                ? t("prescriptionDate")
                : t("visitingDate")}
            </TableSortLabel>
          </StyledCell>
          <StyledCell
            onClick={() => setColumn(["amount", sortDirection])}
            sx={{ fontWeight: "bold" }}
          >
            <TableSortLabel
              active={column[0] === "amount"}
              onClick={() => setColumn(["amount", sortDirection])}
              direction={returnDirection()}
            >
              {t("amount")}
            </TableSortLabel>
          </StyledCell>
          <StyledCell></StyledCell>
          <StyledCell></StyledCell>
        </StyledRow>
      </TableHead>
    );
  }

  function UpdateTransactionRow(props: { transaction: any }) {
    const [open, setOpen] = useState(false);
    const { transaction } = props;
    return (
      <Fragment>
        <StyledRow
          sx={{
            "& > *": {
              borderBottom: "unset",
              borderTop: "unset",
              fontStyle: "italic",
              color: "#808080",
            },
            textDecorationStyle: "solid",
          }}
        >
          <StyledCell
            sx={{
              borderBottom: "unset",
              textDecorationStyle: "solid",
            }}
          >
            <Stack direction="row">
              <IconButton
                sx={{ ml: "2vw" }}
                color="primary"
                aria-label="expand row"
                size="small"
                onClick={() => setOpen(!open)}
              >
                {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>
              <Typography variant="body2" sx={{ mt: "0.5rem" }}>
                {transaction.careProvider}
              </Typography>
            </Stack>
          </StyledCell>
          <StyledCell
            sx={{
              borderBottom: "unset",
              textDecorationStyle: "solid",
            }}
            id={transaction.transactionId}
          >
            <Typography
              variant="body2"
              sx={{ ml: "2vw", color: "#808080", fontStyle: "italic" }}
            >
              {format(new Date(transaction.transactionDate), "yyyy-MM-dd", {
                locale: sv,
              })}
            </Typography>
          </StyledCell>
          <StyledCell
            sx={{
              borderBottom: "unset",
              textDecorationStyle: "solid",
            }}
            id={transaction.transactionId}
          >
            <Typography variant="body2" sx={{ ml: "2vw", color: "#808080" }}>
              {" "}
              {transaction.amount + " kr"}
            </Typography>
          </StyledCell>
        </StyledRow>
        <StyledRow>
          <StyledCell
            sx={{
              borderBottom: "unset",
              textDecorationStyle: "solid",
              fontStyle: "italic",
            }}
            style={{ paddingBottom: 0, paddingTop: 0 }}
            colSpan={6}
          >
            <Collapse
              key={transaction.fromDate}
              in={open}
              timeout="auto"
              unmountOnExit
            >
              <ShowTransaction
                key={transaction.fromDate}
                transaction={transaction}
                cardTypeId={extendedCard?.cardType?.id}
              />
            </Collapse>
          </StyledCell>
        </StyledRow>
      </Fragment>
    );
  }

  function Row(props: { transaction: any; expired: any }) {
    const { transaction } = props;
    const { expired } = props;

    const [open, setOpen] = useState(false);
    const updatedTransactions = sortedList?.filter(
      (trans: any) =>
        trans.transactionId === transaction.transactionId &&
        trans.status === "UPDATED"
    );

    return (
      <Fragment>
        <StyledRow
          key={transaction.transactionId}
          hover={true}
          sx={{
            "& > *": { borderBottom: "unset" },
            textDecorationStyle: "solid",
            textDecorationLine:
              transaction.status === "DELETED" ? "line-through" : null,
            fontStyle: transaction.status !== "ACTIVE" ? "italic" : null,
            color: expired
              ? "red"
              : transaction.status !== "ACTIVE"
              ? "#808080"
              : null,
          }}
        >
          <StyledCell
            sx={{
              maxWidth: "10vh",
              minWidth: "10vh",
              borderBottom: "unset",
              textDecorationStyle: "solid",
              color: expired
                ? "red"
                : transaction.status !== "ACTIVE"
                ? "#808080"
                : null,
            }}
          >
            <IconButton
              color="primary"
              aria-label="expand row"
              size="small"
              onClick={() => setOpen(!open)}
            >
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
            {transaction.careProvider}
          </StyledCell>
          <StyledCell
            id={transaction.transactionId}
            sx={{
              color: expired
                ? "red"
                : transaction.status !== "ACTIVE"
                ? "#808080"
                : null,
              borderBottom: "unset",
              textDecorationStyle: "solid",
            }}
          >
            {format(new Date(transaction.transactionDate), "yyyy-MM-dd", {
              locale: sv,
            })}
          </StyledCell>
          <StyledCell
            id={transaction.transactionId}
            sx={{
              color: expired
                ? "red"
                : transaction.status !== "ACTIVE"
                ? "#808080"
                : null,
              borderBottom: "unset",
              textDecorationStyle: "solid",
            }}
          >
            {transaction.amount + " kr"}
          </StyledCell>
          {isTransactionEditable(
            transaction,
            session.currentUser,
            session.selectedCarePrincipal.id
          ) ? (
            <Fragment>
              <StyledCell align="left">
                <Tooltip title={t<string>("edit")}>
                  <IconButton onClick={() => updateTransaction(transaction)}>
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              </StyledCell>
              <StyledCell align="left">
                <Tooltip title={t<string>("remove")}>
                  <IconButton onClick={() => deleteTransaction(transaction)}>
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </StyledCell>
            </Fragment>
          ) : null}
        </StyledRow>
        <StyledRow>
          <StyledCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
            <Collapse
              key={transaction.fromDate}
              in={open}
              timeout="auto"
              unmountOnExit
            >
              <ShowTransaction
                key={transaction.fromDate}
                transaction={transaction}
                cardTypeId={extendedCard?.cardType?.id}
              />
            </Collapse>
          </StyledCell>
        </StyledRow>
        {showChanged &&
          updatedTransactions &&
          updatedTransactions.length > 0  &&
          updatedTransactions.map((trans: any) => (
            <UpdateTransactionRow transaction={trans}></UpdateTransactionRow>
          ))}
      </Fragment>
    );
  }

  return (
    <>
      <FormControlLabel
        control={
          <GuiSwitch onChange={(e) => setShowChanged(e.target.checked)} />
        }
        label={t<string>("searchCard.manageTxns.showChanged")}
      />
      <FormControlLabel
        control={
          <GuiSwitch
            defaultChecked
            onChange={(e) => setShowInactive(e.target.checked)}
          />
        }
        label={t<string>("searchCard.manageTxns.showInactive")}
      />

      {extendedCard ? (
        <TableContainer component={Paper}>
          <Table>
            <Head />
            <TableBody>
              {sortedList &&
                sortedList.map((trans: any, i: number) =>
                  trans.status === "ACTIVE" ? (
                    <Row
                      key={trans.transactionId + trans.status + i}
                      expired={false}
                      transaction={trans}
                    ></Row>
                  ) : showChanged && trans.status === "DELETED" ? (
                    <Row
                      key={trans.transactionId + trans.status + i}
                      expired={false}
                      transaction={trans}
                    ></Row>
                  ) : null
                )}
            </TableBody>
          </Table>
        </TableContainer>
      ) : null}

      {extendedCard && extendedCard.expiredTransactions[0] && showInactive ? (
        <TableContainer>
          <Fragment>
            <Typography sx={{ margin: "1vh 0 1vh 0 " }} variant="h5">
              Avgifter utanför aktuell period
            </Typography>
            <Table>
              <Head />
              <TableBody>
                {expiredList &&
                  expiredList.map((trans: any, i: number) =>
                    trans.status === "ACTIVE" ? (
                      <Row
                        key={trans.transactionId + trans.status + i}
                        expired={true}
                        transaction={trans}
                      ></Row>
                    ) : showChanged && trans.status === "DELETED" ? (
                      <Row
                        key={trans.transactionId + trans.status + i}
                        expired={true}
                        transaction={trans}
                      ></Row>
                    ) : null
                  )}
              </TableBody>
            </Table>
          </Fragment>
        </TableContainer>
      ) : showInactive ? (
        <Typography sx={{ margin: "1vh 0 1vh 0 " }} variant="h5">
          Det finns inga inaktuella avgifter
        </Typography>
      ) : null}

      <Stack>
        <Button
          sx={{ marginTop: "1.6rem", maxWidth: "6rem" }}
          component={NavLink}
          to={"/search-card"}
          variant="contained"
        >
          {t("back")}
        </Button>
      </Stack>
    </>
  );
}
